import type { Constructor, MappingDefinition } from './types';
import { MappingConfiguration } from './MappingConfiguration';
import type { MappingProfile } from "./MappingProfile";

export class Mapper {
  private mappings: Map<string, MappingDefinition> = new Map();

  createMap<TSource, TDestination>(
    sourceType: Constructor<TSource>,
    destinationType: Constructor<TDestination>
  ): MappingConfiguration<TSource, TDestination> {
    const config = new MappingConfiguration<TSource, TDestination>(
      this,
      sourceType,
      destinationType
    );
    const key = this.getMappingKey(sourceType, destinationType);
    this.mappings.set(key, config.getDefinition());
    return config;
  }

  createCustomMap<TSource, TDestination>(
    sourceType: Constructor<TSource>,
    destinationType: Constructor<TDestination>,
    customMapper: (source: TSource) => TDestination
  ): void {
    const key = this.getMappingKey(sourceType, destinationType);
    this.mappings.set(key, {
      sourceType,
      destinationType,
      rules: [],
      autoMap: false,
      customMapper,
    });
  }

  map<TSource, TDestination>(
    source: TSource,
    destinationType: Constructor<TDestination>
  ): TDestination {
    if (!source) {
      throw new Error('Source object cannot be null or undefined');
    }
    const sourceType = source.constructor as Constructor<TSource>;
    const key = this.getMappingKey(sourceType, destinationType);
    const definition = this.mappings.get(key);
    if (!definition) {
      throw new Error(
        `No mapping configuration found for ${sourceType.name} -> ${destinationType.name}. Did you forget to call createMap()?`
      );
    }
    if (definition.customMapper) {
      return definition.customMapper(source);
    }
    const destination: any = new destinationType();
    if (definition.autoMap) {
      this.autoMapProperties(source, destination);
    }
    for (const rule of definition.rules) {
      const value = rule.transform(source);
      destination[rule.destinationKey] = value;
    }
    return destination;
  }

  mapArray<TSource, TDestination>(
    sources: TSource[],
    destinationType: Constructor<TDestination>
  ): TDestination[] {
    if (!Array.isArray(sources)) {
      throw new Error('Source must be an array');
    }
    return sources.map(source => this.map(source, destinationType));
  }

  addProfile(profile: MappingProfile): void {
    profile.configure(this);
  }

  clear(): void {
    this.mappings.clear();
  }

  private getMappingKey(
    sourceType: Constructor,
    destinationType: Constructor
  ): string {
    return `${sourceType.name}->${destinationType.name}`;
  }

  private autoMapProperties(source: any, destination: any): void {
    const sourceKeys = Object.keys(source);

    for (const key of sourceKeys) {
      // Vérifie si la propriété existe dans la destination
      // en utilisant le prototype de la classe destination
      if (key in destination || this.hasProperty(destination, key)) {
        destination[key] = source[key];
      }
    }
  }

  private hasProperty(obj: any, key: string): boolean {
    // Vérifie récursivement dans la chaîne de prototypes
    let current = obj;
    while (current) {
      if (current.hasOwnProperty(key)) {
        return true;
      }
      current = Object.getPrototypeOf(current);
    }
    return false;
  }
}
